import { defineStore } from 'pinia'

/**
 * Feed Store (Pinia) - UI State Only
 *
 * This store now only manages UI state. All server data (feed items,
 * pagination, caching) is handled by TanStack Query composables:
 *
 * - resources/js/composables/queries/feeds/useFeeds.ts
 * - resources/js/composables/queries/feeds/useFeedDashboard.ts
 * - resources/js/composables/queries/feeds/useRemoveFeedItem.ts
 * - resources/js/composables/queries/feeds/useFeedEventListeners.ts
 *
 * UI State managed here:
 * - activeTab: Current active tab
 * - globalCoachingSessionId: Special coaching session tracking
 * - currentOrganizationId: Track org changes for cache invalidation
 * - Filter state per feed (for UI persistence)
 * ============================================================================
 */

export const useNewFeedStore = defineStore('useNewFeedStore', {
    state: () => ({
        // Bump this when feed data shape/caching behavior changes.
        // Used to invalidate persisted Pinia feed data across deployments.
        cacheVersion: 7,
        activeTab: 'dashboard.home',

        // Global coaching session ID (set from component)
        globalCoachingSessionId: null,

        // Track current organization ID to detect org changes
        currentOrganizationId: null,

        // Filter state per feed (for UI persistence only)
        // TanStack Query uses these as query parameters
        channelMediaFeed: {
            filters: {},
        },
        myMediaFeed: {
            filters: {},
        },
    }),

    actions: {
        /**
         * Update active tab
         * @param {string|object} tab - Tab identifier or tab object
         */
        onUpdateActiveTab(tab) {
            // Normalize tab payload: some components pass the whole tab object.
            // `activeTab` MUST be a string key like "dashboard.channel-media"
            const normalizedTab = typeof tab === 'string' ? tab : (tab?.link ?? tab?.route ?? tab?.value);
            this.activeTab = normalizedTab;

            // IMPORTANT: Do not mutate browser history here.
            // Tab URL changes should be handled by Inertia navigation (links) to keep $page.url in sync.
        },

        /**
         * Sync activeTab based on current URL pathname
         */
        syncActiveTabFromUrl() {
            if (typeof window === 'undefined') return;

            const pathname = window.location.pathname;
            const urlToTabMap = {
                '/dashboard/home': 'dashboard.home',
                '/dashboard/channel-media': 'dashboard.channel-media',
                '/dashboard/my-media': 'dashboard.my-media',
                '/dashboard/coaching-feed': 'dashboard.coaching-feed',
            };

            // Check if current pathname matches any of our routes
            if (urlToTabMap[pathname]) {
                this.activeTab = urlToTabMap[pathname];
            }
        },

        /**
         * Set global coaching session ID
         * @param {number|null} id - Coaching session ID or null
         */
        setGlobalCoachingSessionId(id) {
            this.globalCoachingSessionId = id;
        },

        /**
         * Check if organization has changed and track the new org ID
         * TanStack Query handles cache invalidation via organizationId computed ref
         * @param {number|null} newOrgId - New organization ID
         */
        checkAndHandleOrgChange(newOrgId) {
            // Track org ID for reference (TanStack Query uses this via computed ref)
            this.currentOrganizationId = newOrgId;
        },

        /**
         * Update filters for a feed
         * @param {string} feedType - 'channelMediaFeed' or 'myMediaFeed'
         * @param {object} filters - Filter object
         */
        updateFilters(feedType, filters) {
            if (feedType === 'channelMediaFeed' || feedType === 'myMediaFeed') {
                this[feedType].filters = { ...filters };
            }
        },

        /**
         * Clone filters helper
         * @param {object} filters - Filters to clone
         * @returns {object} - Cloned filters
         */
        cloneFilters(filters = {}) {
            const cloned = {};
            Object.keys(filters || {}).forEach((key) => {
                const value = filters[key];
                cloned[key] = Array.isArray(value) ? [...value] : value;
            });
            return cloned;
        },

        /**
         * Clear filters for a feed
         * @param {string} feedType - 'channelMediaFeed' or 'myMediaFeed'
         */
        clearFilters(feedType) {
            if (feedType === 'channelMediaFeed' || feedType === 'myMediaFeed') {
                this[feedType].filters = {};
            }
        },

        /**
         * Check feed version in background (scanning Redis) even when returning cached data.
         * This allows offline users to detect cache changes when they come back online.
         * Makes a lightweight API call (per_page=1) to just check meta.feed_version.
         */
        checkFeedVersionInBackground(feedState, apiCall) {
            // Only check if we have cached data (otherwise we'll make a real API call anyway)
            if (!feedState.data || feedState.data.length === 0) {
                return;
            }

            // Make lightweight background API call to check feed_version
            // Use minimal data (per_page=1) to reduce payload size
            apiCall(1, false)
                .then(({ data: responseData }) => {
                    const responseFeedVersion = responseData?.meta?.feed_version;
                    if (responseFeedVersion !== undefined && responseFeedVersion !== null) {
                        // If we don't have a stored version yet, store it (first time)
                        if (feedState.feedVersion === null) {
                            feedState.feedVersion = responseFeedVersion;
                        } else if (feedState.feedVersion !== responseFeedVersion) {
                            // Feed version changed - backend cache was invalidated
                            // Clear cached data and timestamps
                            feedState.data = [];
                            feedState.cacheTimestamp = null;
                            // Update stored version after triggering refresh
                            feedState.feedVersion = responseFeedVersion;
                        } else {
                            // Versions match, no change needed
                            feedState.feedVersion = responseFeedVersion;
                        }
                    }
                })
                .catch((error) => {
                    // Silently fail - this is just a background check
                    // Don't spam console with errors
                });
        },
    },

    persist: {
        key: 'feed-store-ui',
        storage: typeof window !== 'undefined' ? localStorage : undefined,
        paths: [
            'activeTab',
            'channelMediaFeed.filters',
            'myMediaFeed.filters',
            'globalCoachingSessionId',
        ],
        beforeRestore: () => {
            if (typeof window === 'undefined') return;

            try {
                const oldStoreKey = 'feed-store';
                if (localStorage.getItem(oldStoreKey)) {
                    localStorage.removeItem(oldStoreKey);
                }

                // Normalize persisted activeTab if it was accidentally stored as an object.
                // This prevents pagination/feed mapping from breaking after reload.
                const stored = JSON.parse(localStorage.getItem('feed-store-ui') || '{}');
                const activeTab = stored?.activeTab;
                if (activeTab && typeof activeTab === 'object') {
                    stored.activeTab = activeTab.link ?? activeTab.route ?? activeTab.value ?? 'dashboard.home';
                    localStorage.setItem('feed-store-ui', JSON.stringify(stored));
                }
            } catch (e) {
                // ignore localStorage errors
            }
        },
    },
})
